import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ToplineCardProps {
  title: string
  value: number
  previousValue?: number
  previousLabel?: string
  loading?: boolean
  showChange?: boolean
  className?: string
  size?: 'default' | 'large'
}

/**
 * Format a number for display
 */
function formatValue(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toLocaleString()
}

/**
 * Calculate percentage change between two values
 */
function calculatePercentChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return Math.round(((current - previous) / previous) * 100)
}

/**
 * Compact topline metric card showing a large number with optional week-over-week change
 */
export function ToplineCard({
  title,
  value,
  previousValue,
  previousLabel = 'last week',
  loading = false,
  showChange = true,
  className,
  size = 'default',
}: ToplineCardProps) {
  const change = previousValue !== undefined ? calculatePercentChange(value, previousValue) : 0
  const hasChange = showChange && previousValue !== undefined && change !== 0
  const isPositive = change > 0
  const isNegative = change < 0

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02] p-4',
        'transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.03]',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider">
          {title}
        </span>
        {!loading && hasChange && (
          <div
            className={cn(
              'flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full',
              isPositive && 'text-primary bg-primary/10',
              isNegative && 'text-red-400 bg-red-400/10',
              !isPositive && !isNegative && 'text-muted-foreground/50 bg-white/[0.03]'
            )}
          >
            {isPositive ? (
              <TrendingUp className="h-3 w-3" />
            ) : isNegative ? (
              <TrendingDown className="h-3 w-3" />
            ) : (
              <Minus className="h-3 w-3" />
            )}
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>

      {/* Value */}
      {loading ? (
        <div className="flex items-center gap-2">
          <Loader2 className={cn('animate-spin text-muted-foreground/40', size === 'large' ? 'h-8 w-8' : 'h-6 w-6')} />
        </div>
      ) : (
        <div className="flex items-baseline gap-2">
          <span className={cn(
            'font-semibold text-foreground tabular-nums',
            size === 'large' ? 'text-8xl' : 'text-5xl'
          )}>
            {formatValue(value)}
          </span>
          {showChange && previousValue !== undefined && (
            <span className={cn('text-muted-foreground/50', size === 'large' ? 'text-lg' : 'text-base')}>
              vs {formatValue(previousValue)} {previousLabel}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
